/**
 * Per-preset visual treatment for shared store chrome (StoreHeader, ProductCard, etc.).
 * Each entry returns the className strings the component should apply for that preset.
 * Markup stays identical across presets; only the styling layer swaps.
 */

export const HEADER_STYLES = {
  kalki: {
    root: "sticky top-0 z-40 bg-[hsl(var(--kb-bg))]/90 backdrop-blur-xl border-b border-[hsl(var(--kb-border))]",
    showAnnouncement: true,
    announcementBar: "bg-[hsl(var(--kb-primary))] text-white text-[10px] md:text-[11px] tracking-[0.2em] uppercase",
    logo: "font-display text-2xl md:text-4xl tracking-tight flex items-center gap-2",
    searchWrap: "hidden md:flex flex-1 max-w-xl items-center gap-2 border-b border-[hsl(var(--kb-border))] focus-within:border-[hsl(var(--kb-primary))]",
    searchInput: "flex-1 bg-transparent py-2 text-sm focus:outline-none",
    nav: "hidden md:flex items-center justify-center gap-10 px-8 pb-4 text-[13px] tracking-[0.15em] uppercase relative",
    navLink: "flex items-center gap-1 hover:text-[hsl(var(--kb-primary))] transition-colors py-2",
    navLinkActive: "text-[hsl(var(--kb-primary))]",
    iconColor: "",
  },

  myntra: {
    root: "sticky top-0 z-40 bg-white shadow-md border-b border-[hsl(var(--kb-border))]",
    showAnnouncement: true,
    announcementBar: "bg-gradient-to-r from-[hsl(var(--kb-primary))] to-[hsl(var(--kb-accent))] text-white text-[11px] tracking-wide font-semibold",
    logo: "font-display text-xl md:text-2xl font-extrabold uppercase tracking-tight text-[hsl(var(--kb-primary))] flex items-center gap-2",
    searchWrap: "hidden md:flex flex-1 max-w-2xl items-center gap-2 bg-[hsl(var(--kb-surface-alt))] rounded-md px-3 focus-within:ring-2 focus-within:ring-[hsl(var(--kb-primary))]/30",
    searchInput: "flex-1 bg-transparent py-2.5 text-sm focus:outline-none",
    nav: "hidden md:flex items-center justify-center gap-7 px-8 pb-3 text-[11px] tracking-[0.12em] uppercase font-bold relative",
    navLink: "flex items-center gap-1 hover:text-[hsl(var(--kb-primary))] transition-colors py-2",
    navLinkActive: "text-[hsl(var(--kb-primary))]",
    iconColor: "",
  },

  ajio: {
    root: "sticky top-0 z-40 bg-[hsl(var(--kb-primary))] text-white",
    showAnnouncement: true,
    announcementBar: "bg-[hsl(var(--kb-accent))] text-black text-[10px] tracking-[0.3em] uppercase font-bold",
    logo: "font-display text-xl md:text-2xl font-extrabold uppercase tracking-widest text-white flex items-center gap-2",
    searchWrap: "hidden md:flex flex-1 max-w-xl items-center gap-2 border border-white/30 rounded-none px-3 focus-within:border-[hsl(var(--kb-accent))]",
    searchInput: "flex-1 bg-transparent py-2 text-sm focus:outline-none placeholder:text-white/60",
    nav: "hidden md:flex items-center justify-center gap-7 px-8 pb-3 text-[11px] tracking-[0.25em] uppercase font-bold relative",
    navLink: "flex items-center gap-1 hover:text-[hsl(var(--kb-accent))] transition-colors py-2",
    navLinkActive: "text-[hsl(var(--kb-accent))]",
    iconColor: "text-white",
  },

  mango: {
    root: "sticky top-0 z-40 bg-white border-b border-[hsl(var(--kb-border))]",
    showAnnouncement: false,
    announcementBar: "",
    logo: "font-display text-xl md:text-2xl font-light tracking-[0.3em] uppercase flex items-center gap-2",
    searchWrap: "hidden md:flex flex-1 max-w-md items-center gap-2 border-b border-black focus-within:border-black",
    searchInput: "flex-1 bg-transparent py-2 text-xs tracking-widest uppercase focus:outline-none placeholder:text-black/40",
    nav: "hidden md:flex items-center justify-center gap-12 px-8 pb-3 text-[11px] tracking-[0.4em] uppercase font-light relative",
    navLink: "flex items-center gap-1 hover:text-black/60 transition-colors py-2",
    navLinkActive: "text-black underline underline-offset-8",
    iconColor: "",
  },

  biba: {
    root: "sticky top-0 z-40 bg-[hsl(var(--kb-bg))]/95 backdrop-blur-xl border-b-2 border-[hsl(var(--kb-primary))]/20",
    showAnnouncement: true,
    announcementBar: "bg-[hsl(var(--kb-primary))] text-white text-[11px] tracking-wide",
    logo: "font-display italic text-2xl md:text-3xl text-[hsl(var(--kb-primary))] flex items-center gap-2",
    searchWrap: "hidden md:flex flex-1 max-w-xl items-center gap-2 border border-[hsl(var(--kb-border))] rounded-full px-4 focus-within:border-[hsl(var(--kb-primary))]",
    searchInput: "flex-1 bg-transparent py-2 text-sm focus:outline-none",
    nav: "hidden md:flex items-center justify-center gap-2 px-8 pb-3 text-[12px] tracking-wider uppercase relative",
    navLink: "flex items-center gap-1 px-4 py-2 rounded-full hover:bg-[hsl(var(--kb-primary))]/10 hover:text-[hsl(var(--kb-primary))] transition-colors",
    navLinkActive: "text-[hsl(var(--kb-primary))] bg-[hsl(var(--kb-primary))]/10",
    iconColor: "",
  },

  neerus: {
    root: "sticky top-0 z-40 bg-[hsl(var(--kb-primary))] text-white border-b border-[hsl(var(--kb-accent))]/30",
    showAnnouncement: true,
    announcementBar: "bg-black/30 text-[hsl(var(--kb-accent))] text-[10px] tracking-[0.4em] uppercase",
    logo: "font-display text-xl md:text-2xl uppercase tracking-[0.2em] text-[hsl(var(--kb-accent))] flex items-center gap-2",
    searchWrap: "hidden md:flex flex-1 max-w-xl items-center gap-2 border-b border-[hsl(var(--kb-accent))]/40 focus-within:border-[hsl(var(--kb-accent))]",
    searchInput: "flex-1 bg-transparent py-2 text-sm focus:outline-none placeholder:text-white/60",
    nav: "hidden md:flex items-center justify-center gap-8 px-8 pb-3 text-[11px] tracking-[0.3em] uppercase relative",
    navLink: "flex items-center gap-1 hover:text-[hsl(var(--kb-accent))] transition-colors py-2 border-b border-transparent hover:border-[hsl(var(--kb-accent))]/60",
    navLinkActive: "text-[hsl(var(--kb-accent))]",
    iconColor: "text-white",
  },

  flipkart: {
    root: "sticky top-0 z-40 bg-gradient-to-b from-[hsl(var(--kb-primary))]/15 via-[hsl(var(--kb-bg))] to-[hsl(var(--kb-bg))] border-b border-[hsl(var(--kb-border))]",
    showAnnouncement: true,
    announcementBar: "bg-[hsl(var(--kb-primary))] text-white text-[11px] font-medium tracking-wide",
    logo: "font-display text-xl md:text-2xl font-extrabold italic tracking-tight text-[hsl(var(--kb-primary))] flex items-center gap-2",
    searchWrap: "hidden md:flex flex-1 max-w-2xl items-center gap-2 bg-white border-2 border-[hsl(var(--kb-primary))]/40 rounded-lg px-4 focus-within:border-[hsl(var(--kb-primary))] shadow-sm",
    searchInput: "flex-1 bg-transparent py-2.5 text-sm focus:outline-none",
    nav: "hidden md:flex items-center justify-center gap-6 px-8 pb-3 text-[12px] font-semibold relative",
    navLink: "flex items-center gap-1 hover:text-[hsl(var(--kb-primary))] transition-colors py-2",
    navLinkActive: "text-[hsl(var(--kb-primary))] border-b-2 border-[hsl(var(--kb-primary))]",
    iconColor: "",
  },

  amazon: {
    root: "sticky top-0 z-40 bg-[hsl(var(--kb-primary))] text-white",
    showAnnouncement: false,
    announcementBar: "",
    logo: "font-display text-xl md:text-2xl font-bold tracking-tight text-white flex items-center gap-2",
    searchWrap: "hidden md:flex flex-1 max-w-2xl items-center gap-2 bg-white rounded-lg px-4 focus-within:ring-2 focus-within:ring-[hsl(var(--kb-accent))]",
    searchInput: "flex-1 bg-transparent py-2.5 text-sm focus:outline-none text-[hsl(var(--kb-text))] placeholder:text-[hsl(var(--kb-muted))]",
    nav: "hidden md:flex items-center justify-center gap-6 px-8 pb-3 text-[12px] font-semibold relative",
    navLink: "flex items-center gap-1 hover:text-[hsl(var(--kb-accent))] transition-colors py-2",
    navLinkActive: "text-[hsl(var(--kb-accent))]",
    iconColor: "text-white",
  },

  asfashion: {
    // Boutique-ethnic: white sticky header, marquee is charcoal with uppercase
    // wide-tracked pink-tick separators (see asfashion.store).
    root: "sticky top-0 z-40 bg-white border-b border-[hsl(var(--kb-border))]",
    showAnnouncement: true,
    announcementBar: "bg-[hsl(var(--kb-primary))] text-white text-[11px] font-semibold tracking-[0.08em] uppercase",
    logo: "font-display text-xl md:text-2xl tracking-wide flex items-center gap-2 text-[hsl(var(--kb-text))]",
    searchWrap: "hidden md:flex flex-1 max-w-xl items-center gap-2 border border-[hsl(var(--kb-border))] px-3 focus-within:border-[hsl(var(--kb-text))]",
    searchInput: "flex-1 bg-transparent py-2 text-sm focus:outline-none",
    // Uppercase serif nav with wide tracking, hairline underline on hover
    nav: "hidden md:flex items-center justify-center gap-8 px-8 pb-3 text-[12px] tracking-[0.15em] uppercase relative",
    navLink: "flex items-center gap-1 py-2 border-b border-transparent hover:border-[hsl(var(--kb-text))] transition-colors",
    navLinkActive: "border-b border-[hsl(var(--kb-text))]",
    iconColor: "text-[hsl(var(--kb-text))]",
  },
};

export const PRODUCT_CARD_STYLES = {
  kalki: {
    discountBadge: "bg-white/90 text-[hsl(var(--kb-primary))] px-2 py-1 text-[10px] uppercase tracking-wider",
    seller: "text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--kb-muted))] mb-1",
    title: "font-display text-lg leading-tight mb-2 line-clamp-1",
    price: "text-[hsl(var(--kb-primary))] font-medium",
    cardWrap: "group block kb-fade-in",
    imageWrap: "relative overflow-hidden aspect-[3/4] bg-[hsl(var(--kb-surface-alt))]",
    info: "pt-4",
  },
  myntra: {
    discountBadge: "bg-[hsl(var(--kb-primary))] text-white px-2 py-0.5 text-[11px] font-bold rounded",
    seller: "text-[11px] font-semibold uppercase text-[hsl(var(--kb-text))] mb-0.5 line-clamp-1",
    title: "text-sm text-[hsl(var(--kb-muted))] line-clamp-1 mb-1.5",
    price: "text-[hsl(var(--kb-text))] font-bold text-base",
    cardWrap: "group block kb-fade-in bg-white p-2 rounded-md",
    imageWrap: "relative overflow-hidden aspect-[3/4] bg-[hsl(var(--kb-surface-alt))] rounded-md",
    info: "pt-3 px-1",
  },
  ajio: {
    discountBadge: "bg-[hsl(var(--kb-accent))] text-black px-2 py-1 text-[10px] font-extrabold uppercase tracking-wide",
    seller: "text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--kb-muted))] mb-1",
    title: "font-display text-base font-extrabold uppercase tracking-tight mb-2 line-clamp-1",
    price: "text-[hsl(var(--kb-text))] font-extrabold",
    cardWrap: "group block kb-fade-in",
    imageWrap: "relative overflow-hidden aspect-[3/4] bg-[hsl(var(--kb-surface-alt))]",
    info: "pt-4",
  },
  mango: {
    discountBadge: "bg-black text-white px-2 py-1 text-[10px] tracking-[0.2em] uppercase",
    seller: "text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--kb-muted))] mb-1",
    title: "font-display text-base font-light tracking-wide mb-2 line-clamp-1",
    price: "text-[hsl(var(--kb-text))] font-light",
    cardWrap: "group block kb-fade-in",
    imageWrap: "relative overflow-hidden aspect-[3/4] bg-[hsl(var(--kb-surface-alt))]",
    info: "pt-5",
  },
  biba: {
    discountBadge: "bg-[hsl(var(--kb-primary))] text-white px-3 py-1 text-[10px] uppercase tracking-wider rounded-full",
    seller: "text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--kb-muted))] mb-1",
    title: "font-display italic text-lg leading-tight mb-2 line-clamp-1 text-[hsl(var(--kb-primary))]",
    price: "text-[hsl(var(--kb-text))] font-medium",
    cardWrap: "group block kb-fade-in bg-white rounded-2xl overflow-hidden border border-[hsl(var(--kb-border))]",
    imageWrap: "relative overflow-hidden aspect-[3/4] bg-[hsl(var(--kb-surface-alt))]",
    info: "px-3 py-3 text-center",
  },
  neerus: {
    discountBadge: "border border-[hsl(var(--kb-accent))] bg-[hsl(var(--kb-primary))]/85 text-[hsl(var(--kb-accent))] px-2 py-1 text-[10px] uppercase tracking-[0.2em]",
    seller: "text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--kb-accent))] mb-1",
    title: "font-display text-base uppercase tracking-wider mb-2 line-clamp-1",
    price: "text-[hsl(var(--kb-primary))] font-semibold",
    cardWrap: "group block kb-fade-in",
    imageWrap: "relative overflow-hidden aspect-[3/4] bg-[hsl(var(--kb-surface-alt))] border border-[hsl(var(--kb-accent))]/30",
    info: "pt-4",
  },
  flipkart: {
    discountBadge: "bg-[hsl(var(--kb-primary))] text-white px-2 py-0.5 text-[11px] font-bold rounded-md",
    seller: "text-[11px] font-semibold text-[hsl(var(--kb-muted))] mb-0.5 line-clamp-1",
    title: "text-sm text-[hsl(var(--kb-text))] line-clamp-2 mb-1.5 font-medium",
    price: "text-[hsl(var(--kb-text))] font-bold text-base",
    cardWrap: "group block kb-fade-in bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-[hsl(var(--kb-border))]",
    imageWrap: "relative overflow-hidden aspect-square bg-white rounded-t-lg",
    info: "p-3",
  },
  amazon: {
    discountBadge: "bg-[hsl(var(--kb-primary))] text-white px-2 py-0.5 text-[11px] font-bold rounded-md",
    seller: "text-[11px] font-medium text-[hsl(var(--kb-muted))] mb-0.5 line-clamp-1",
    title: "text-sm text-[hsl(var(--kb-text))] line-clamp-2 mb-1 font-semibold",
    price: "text-[hsl(var(--kb-primary))] font-bold text-base",
    cardWrap: "group block kb-fade-in bg-white rounded-xl border border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]/40 transition-colors",
    imageWrap: "relative overflow-hidden aspect-square bg-white rounded-t-xl",
    info: "p-3",
  },
  asfashion: {
    // Hot-pink discount pill on cream, sharp-edged 3:4 card, price row centred.
    discountBadge: "bg-pink-50 text-[hsl(var(--kb-accent))] border border-pink-100 px-1.5 py-0.5 text-[10px] font-bold tracking-tight",
    seller: "text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--kb-muted))] mb-1",
    title: "text-[11px] sm:text-xs font-medium text-[hsl(var(--kb-text))] leading-snug line-clamp-2 mb-1 group-hover:text-[hsl(var(--kb-accent))] transition-colors",
    price: "text-[hsl(var(--kb-text))] font-bold text-xs sm:text-sm",
    cardWrap: "group block kb-fade-in",
    imageWrap: "relative overflow-hidden aspect-[3/4] bg-[hsl(var(--kb-surface-alt))] border border-[hsl(var(--kb-border))]",
    info: "pt-3 text-center flex flex-col items-center px-1",
  },
};

export function getHeaderStyles(preset) {
  return HEADER_STYLES[preset] || HEADER_STYLES.kalki;
}

export function getProductCardStyles(preset) {
  return PRODUCT_CARD_STYLES[preset] || PRODUCT_CARD_STYLES.kalki;
}

// =====================================================================
// Per-preset Footer treatment — matches the chrome of each header/hero.
// `root`         outer footer wrapper
// `brandWrap`    title block ('Brand' column heading text style)
// `brandText`    description/tagline copy
// `colHeading`   eyebrow text for "Shop", "Company", "Stay in Touch"
// `linkClass`    nav link styling
// `divider`      bottom strip border + colour
// `microCopy`    bottom-bar copyright/legal text
// `newsletterWrap` newsletter form container styles
// `newsletterBtn`   newsletter join button styles
// =====================================================================

export const FOOTER_STYLES = {
  kalki: {
    root: "bg-[hsl(var(--kb-surface-alt))] border-t border-[hsl(var(--kb-border))] mt-24",
    brandWrap: "font-display text-3xl mb-4",
    brandText: "text-sm text-[hsl(var(--kb-muted))] leading-relaxed",
    colHeading: "overline mb-4",
    linkClass: "hover:text-[hsl(var(--kb-primary))] transition-colors",
    divider: "border-t border-[hsl(var(--kb-border))]",
    microCopy: "text-xs text-[hsl(var(--kb-muted))]",
    newsletterWrap: "flex border-b border-[hsl(var(--kb-border))]",
    newsletterBtn: "text-xs uppercase tracking-wider hover:text-[hsl(var(--kb-primary))]",
  },
  myntra: {
    root: "bg-white border-t border-[hsl(var(--kb-border))] mt-24 shadow-[inset_0_4px_0_0_hsl(var(--kb-primary))]",
    brandWrap: "font-display text-2xl font-extrabold uppercase tracking-tight text-[hsl(var(--kb-primary))] mb-4",
    brandText: "text-sm text-[hsl(var(--kb-muted))] leading-relaxed",
    colHeading: "text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--kb-text))] mb-4",
    linkClass: "hover:text-[hsl(var(--kb-primary))] font-medium transition-colors",
    divider: "border-t border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))]",
    microCopy: "text-xs text-[hsl(var(--kb-muted))] font-medium",
    newsletterWrap: "flex bg-[hsl(var(--kb-surface-alt))] rounded-md px-3",
    newsletterBtn: "text-xs uppercase tracking-wide font-bold text-[hsl(var(--kb-primary))]",
  },
  ajio: {
    root: "bg-[hsl(var(--kb-primary))] text-white mt-24",
    brandWrap: "font-display text-2xl font-extrabold uppercase tracking-widest mb-4",
    brandText: "text-sm text-white/70 leading-relaxed",
    colHeading: "text-[11px] font-bold uppercase tracking-[0.25em] text-[hsl(var(--kb-accent))] mb-4",
    linkClass: "hover:text-[hsl(var(--kb-accent))] transition-colors",
    divider: "border-t border-white/15",
    microCopy: "text-xs text-white/60",
    newsletterWrap: "flex border border-white/30 rounded-none px-3 focus-within:border-[hsl(var(--kb-accent))]",
    newsletterBtn: "text-xs uppercase tracking-[0.2em] font-bold text-[hsl(var(--kb-accent))]",
  },
  mango: {
    root: "bg-white border-t border-black/80 mt-24",
    brandWrap: "font-display text-2xl font-light tracking-[0.3em] uppercase mb-4",
    brandText: "text-sm text-[hsl(var(--kb-muted))] leading-relaxed",
    colHeading: "text-[10px] uppercase tracking-[0.4em] text-black mb-4 font-light",
    linkClass: "hover:text-black/60 transition-colors tracking-wide",
    divider: "border-t border-[hsl(var(--kb-border))]",
    microCopy: "text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--kb-muted))]",
    newsletterWrap: "flex border-b border-black",
    newsletterBtn: "text-[10px] uppercase tracking-[0.3em] font-light",
  },
  biba: {
    root: "bg-[hsl(var(--kb-surface-alt))] border-t-2 border-[hsl(var(--kb-primary))]/30 mt-24",
    brandWrap: "font-display italic text-3xl text-[hsl(var(--kb-primary))] mb-4",
    brandText: "text-sm text-[hsl(var(--kb-muted))] leading-relaxed",
    colHeading: "text-[12px] uppercase tracking-wider text-[hsl(var(--kb-primary))] mb-4 font-medium",
    linkClass: "hover:text-[hsl(var(--kb-primary))] transition-colors",
    divider: "border-t border-[hsl(var(--kb-primary))]/20",
    microCopy: "text-xs text-[hsl(var(--kb-muted))]",
    newsletterWrap: "flex border border-[hsl(var(--kb-border))] rounded-full px-4 focus-within:border-[hsl(var(--kb-primary))]",
    newsletterBtn: "text-xs uppercase tracking-wider text-[hsl(var(--kb-primary))]",
  },
  neerus: {
    root: "bg-[hsl(var(--kb-primary))] text-white border-t border-[hsl(var(--kb-accent))]/30 mt-24",
    brandWrap: "font-display text-2xl uppercase tracking-[0.2em] text-[hsl(var(--kb-accent))] mb-4",
    brandText: "text-sm text-white/70 leading-relaxed",
    colHeading: "text-[11px] uppercase tracking-[0.3em] text-[hsl(var(--kb-accent))] mb-4",
    linkClass: "hover:text-[hsl(var(--kb-accent))] transition-colors border-b border-transparent hover:border-[hsl(var(--kb-accent))]/60",
    divider: "border-t border-[hsl(var(--kb-accent))]/20",
    microCopy: "text-xs text-white/60 tracking-wide",
    newsletterWrap: "flex border-b border-[hsl(var(--kb-accent))]/40 focus-within:border-[hsl(var(--kb-accent))]",
    newsletterBtn: "text-xs uppercase tracking-[0.25em] text-[hsl(var(--kb-accent))]",
  },
  flipkart: {
    root: "bg-white border-t-4 border-[hsl(var(--kb-primary))] mt-24",
    brandWrap: "font-display text-2xl font-extrabold italic text-[hsl(var(--kb-primary))] mb-4",
    brandText: "text-sm text-[hsl(var(--kb-muted))] leading-relaxed",
    colHeading: "text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--kb-text))] mb-4",
    linkClass: "hover:text-[hsl(var(--kb-primary))] font-medium transition-colors",
    divider: "border-t border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))]",
    microCopy: "text-xs text-[hsl(var(--kb-muted))] font-medium",
    newsletterWrap: "flex bg-[hsl(var(--kb-surface-alt))] rounded-lg px-4 border border-[hsl(var(--kb-border))]",
    newsletterBtn: "text-xs uppercase tracking-wide font-bold text-[hsl(var(--kb-primary))]",
  },
  amazon: {
    root: "bg-[hsl(var(--kb-primary))] text-white mt-24",
    brandWrap: "font-display text-2xl font-bold mb-4",
    brandText: "text-sm text-white/80 leading-relaxed",
    colHeading: "text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--kb-accent))] mb-4",
    linkClass: "hover:text-[hsl(var(--kb-accent))] transition-colors",
    divider: "border-t border-white/15",
    microCopy: "text-xs text-white/70",
    newsletterWrap: "flex bg-white rounded-lg px-4 focus-within:ring-2 focus-within:ring-[hsl(var(--kb-accent))]",
    newsletterBtn: "text-xs uppercase tracking-wide font-bold bg-[hsl(var(--kb-accent))] text-[hsl(var(--kb-text))] px-4 py-2 rounded",
  },
  asfashion: {
    // Charcoal footer on cream, editorial serif brand mark, pink accent
    // for column headings + newsletter CTA.
    root: "bg-[hsl(var(--kb-primary))] text-white mt-24",
    brandWrap: "font-display text-2xl tracking-wide mb-4",
    brandText: "text-sm text-white/70 leading-relaxed",
    colHeading: "text-[11px] font-semibold uppercase tracking-[0.2em] text-[hsl(var(--kb-accent))] mb-4",
    linkClass: "hover:text-[hsl(var(--kb-accent))] transition-colors",
    divider: "border-t border-white/10",
    microCopy: "text-xs text-white/60 tracking-wide",
    newsletterWrap: "flex border-b border-white/30 focus-within:border-[hsl(var(--kb-accent))]",
    newsletterBtn: "text-xs uppercase tracking-[0.2em] font-bold text-[hsl(var(--kb-accent))]",
  },
};

export function getFooterStyles(preset) {
  return FOOTER_STYLES[preset] || FOOTER_STYLES.kalki;
}
